/**
 * Conversion Engine — Booth Check-in (Stage 2a). Mobile-first CRM page.
 *
 * A USLS rep at a gun-show booth:
 *   1. Picks the event ONCE while online (persisted locally so it survives a
 *      reload after going offline).
 *   2. Types an invite voucher code → member info populates (online).
 *   3. Toggles facts: attended / brought a friend / friend joined USLS.
 *   4. Submits. The server computes & escalates the voucher value from the
 *      reward matrix; the rep never sees or sets a dollar amount.
 *
 * OFFLINE: mirrors /redeem. If the network is down (or the submit fails on the
 * network), the check-in is queued in `checkinQueue` (facts only) and retried
 * automatically on reconnect. Offline submit is "blind" (no member populate) —
 * safe because the server validates + computes on sync.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Html5Qrcode } from "html5-qrcode";
import QRCode from "qrcode";
import { supabase } from "../services/supabase";
import { fetchAllRows } from "../utils/fetchAllRows";
import {
  lookupCheckinVoucher,
  submitCheckin,
  generateTicket,
  linkFriend,
  newCheckinIdempotencyKey,
  type CheckinContact,
  type CheckinInput,
  type CheckinResultData,
} from "../services/checkin.service";
import {
  enqueue as enqueueCheckin,
  processQueue as processCheckinQueue,
  retryEntry as retryQueueEntry,
  removeEntry as removeQueueEntry,
  getQueue,
  type QueuedCheckin,
} from "../services/checkinQueue";
import {
  eventTypeLabel,
  shouldResetAfterCheckin,
  shouldCollapseSelector,
  summarizeQueue,
  relativeAge,
} from "../utils/boothCheckin";

const EVENT_STORAGE_KEY = "booth_checkin_event_v1";

interface BoothEvent {
  id: string;
  title: string;
  type: string | null;
  conversion_engine_id: string | null;
}

interface LoadedMember {
  contact: CheckinContact;
  voucherCode: string;
  eventId: string | null;
  activationState: string | null;
  currentValue: number | null;
  blockReason?: string;
}

function memberLabel(c: CheckinContact | undefined): string {
  if (!c) {
    return "Member";
  }
  const name = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
  return name || c.email || "Member";
}

// Basic email shape check — blocks obvious typos before we hit the backend.
function isValidEmail(raw: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw.trim());
}

// US phone: exactly 10 digits once separators/“+1” are stripped. Returns the
// 10 digits, or null if the input isn't a valid 10-digit number.
function normalizeUsPhone(raw: string): string | null {
  const digits = raw.replace(/\D/g, "");
  const ten =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  return ten.length === 10 ? ten : null;
}

export default function BoothCheckin() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  // Exit lands on the caller's home: a field MSR is locked out of /dashboard
  // (R8-002), so send them back to their portal.
  const exitTo =
    (user?.role || "").toLowerCase().replace(/[_-]/g, "") === "msr"
      ? "/msr"
      : "/dashboard";
  const [online, setOnline] = useState<boolean>(
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );
  // The full queue (for the inspection panel) — count is derived from it so the
  // banner, summary, and rows can never disagree.
  const [queueItems, setQueueItems] = useState<QueuedCheckin[]>(() =>
    getQueue(),
  );
  const [queueOpen, setQueueOpen] = useState(false);
  const queueCount = queueItems.length;
  // Once an eligible event is chosen, the selector collapses to a compact summary
  // to save phone space; "Change" re-expands it.
  const [selectorExpanded, setSelectorExpanded] = useState(false);

  const [events, setEvents] = useState<BoothEvent[]>([]);
  // Active conversion engine event_types — an event is "eligible" for the
  // conversion booth flow if its type is one of these OR it has its own engine.
  const [engineTypes, setEngineTypes] = useState<string[]>([]);
  // Distinguish "engines loaded (maybe zero)" from "engine lookup failed" so we
  // never silently fall open to showing ALL events online (incl. seminars).
  const [enginesLoaded, setEnginesLoaded] = useState<boolean>(false);
  const [engineLoadError, setEngineLoadError] = useState<boolean>(false);
  const [eventId, setEventId] = useState<string>(
    () =>
      searchParams.get("event") ||
      localStorage.getItem(EVENT_STORAGE_KEY) ||
      "",
  );

  const [code, setCode] = useState<string>("");
  const [member, setMember] = useState<LoadedMember | null>(null);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupMsg, setLookupMsg] = useState<string>("");

  const [attended, setAttended] = useState(true);
  const [broughtFriend, setBroughtFriend] = useState(false);
  const [friendJoined, setFriendJoined] = useState(false);
  // Stage 3a — when "friend joined" is on, the rep identifies the friend (email
  // or their ticket code) so the inviter gets credited at membership confirmation.
  const [friendIdentifier, setFriendIdentifier] = useState("");

  const [submitBusy, setSubmitBusy] = useState(false);
  const [banner, setBanner] = useState<{
    kind: "success" | "queued" | "error";
    text: string;
  } | null>(null);
  // Center-screen confirmation for the result of a check-in submit. Reps on
  // mobile were missing the bottom toast and re-tapping Submit / assuming nothing
  // happened, so the submit OUTCOME is shown as a dismiss-required popup.
  const [confirm, setConfirm] = useState<{
    kind: "success" | "queued" | "error";
    text: string;
  } | null>(null);
  const [lastResult, setLastResult] = useState<CheckinResultData | null>(null);

  // Stage 2b — generate a ticket for a no-code walk-in (online only).
  const [showGenerate, setShowGenerate] = useState(false);
  const [genEmail, setGenEmail] = useState("");
  const [genFirst, setGenFirst] = useState("");
  const [genLast, setGenLast] = useState("");
  const [genPhone, setGenPhone] = useState("");
  const [genBusy, setGenBusy] = useState(false);

  // QR scanning (reuses /redeem's Html5Qrcode). One scanner at a time; `scanTarget`
  // says which field the decoded code fills.
  const [scanTarget, setScanTarget] = useState<null | "code" | "friend">(null);
  const qrRef = useRef<Html5Qrcode | null>(null);
  // After "Generate ticket", the new code's QR (rendered in-app via the `qrcode`
  // lib) so the rep can show/print/download it for the member — no external tool.
  const [generatedQr, setGeneratedQr] = useState<{
    code: string;
    dataUrl: string;
    reused: boolean;
    // Step 3 forward-to-a-friend: the member's referral link + a QR of it (secondary,
    // durable copy is the emailed link). Present only when the voucher has a referral_token.
    referralUrl?: string;
    referralDataUrl?: string;
    emailedTo?: string;
  } | null>(null);

  // Re-read the whole queue after every change (auto-sync, manual retry, dismiss)
  // so the panel + count stay live. Named *Count for the existing call sites.
  const refreshQueueCount = useCallback(() => {
    setQueueItems(getQueue());
  }, []);

  // Load events for the selector (online only). Persist the chosen one.
  useEffect(() => {
    if (!online) {
      return;
    }
    let cancelled = false;
    (async () => {
      // Every live event, paged past PostgREST's 1000-row cap. This used to be
      // `.order("start_date").limit(200)`, but start_date is NULL on every event
      // (start_time is the real column), so the 200 rows were an ARBITRARY
      // subset of all events and today's booth event could be missing.
      const [evs, { data: engines, error: enginesErr }] = await Promise.all([
        fetchAllRows<BoothEvent>((from, to) =>
          supabase
            .from("events")
            .select("id, title, type, conversion_engine_id")
            .not("is_deleted", "is", true)
            .order("start_time", { ascending: false })
            .order("id", { ascending: true })
            .range(from, to),
        ).catch((err) => {
          console.error("Error loading booth events:", err);
          return null;
        }),
        supabase
          .from("conversion_engines")
          .select("event_type")
          .eq("is_active", true),
      ]);
      if (cancelled) {
        return;
      }
      if (evs) {
        setEvents(evs);
      }
      if (enginesErr || !engines) {
        // Couldn't determine eligibility — don't silently show all events as if
        // eligible. We surface a notice and disable generate (lookup still works).
        setEngineLoadError(true);
      } else {
        setEngineLoadError(false);
        setEngineTypes(
          Array.from(
            new Set(
              (engines as { event_type: string | null }[])
                .map((e) => e.event_type)
                .filter((t): t is string => Boolean(t)),
            ),
          ),
        );
      }
      setEnginesLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [online]);

  useEffect(() => {
    if (eventId) {
      localStorage.setItem(EVENT_STORAGE_KEY, eventId);
    }
  }, [eventId]);

  // Online/offline wiring — auto-process the queue on reconnect.
  useEffect(() => {
    const onOnline = async () => {
      setOnline(true);
      const outcome = await processCheckinQueue();
      refreshQueueCount();
      if (outcome.succeeded > 0) {
        setBanner({
          kind: "success",
          text: `${outcome.succeeded} queued check-in${outcome.succeeded > 1 ? "s" : ""} synced.`,
        });
      }
    };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [refreshQueueCount]);

  const resetForNext = () => {
    setCode("");
    setMember(null);
    setLookupMsg("");
    setAttended(true);
    setBroughtFriend(false);
    setFriendJoined(false);
    setFriendIdentifier("");
    setGeneratedQr(null);
  };

  // Refresh the page to a clean state for the next person without a full reload
  // (a reload is unnecessary and would briefly blank the screen). Clears the
  // current member/code/outcomes, the generate form, and any banner/popup, and
  // re-reads the offline queue count. The persisted offline queue is untouched.
  const handleRefresh = () => {
    resetForNext();
    setShowGenerate(false);
    setGenEmail("");
    setGenFirst("");
    setGenLast("");
    setGenPhone("");
    setBanner(null);
    setConfirm(null);
    setLastResult(null);
    refreshQueueCount();
  };

  // ---- QR scanning ---------------------------------------------------------
  // A scanned QR may be the bare code or a URL with ?code=. Normalize to the code.
  const extractCode = (raw: string): string => {
    try {
      const u = new URL(raw);
      const c = u.searchParams.get("code");
      if (c) {
        return c.trim().toUpperCase();
      }
    } catch {
      /* not a URL — fall through */
    }
    return raw.trim().toUpperCase();
  };

  const stopScan = useCallback(async () => {
    if (qrRef.current) {
      try {
        await qrRef.current.stop();
      } catch {
        /* already stopped */
      }
      qrRef.current = null;
    }
    setScanTarget(null);
  }, []);

  const startScan = async (target: "code" | "friend") => {
    if (!window.isSecureContext) {
      setBanner({
        kind: "error",
        text: "Camera needs HTTPS — type the code instead, or use the QA/Prod site.",
      });
      return;
    }
    setScanTarget(target);
    setBanner(null);
    await new Promise((r) => setTimeout(r, 120)); // let the #qr-reader div mount
    try {
      const scanner = new Html5Qrcode("qr-reader");
      qrRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 220, height: 220 } },
        (decoded) => {
          const c = extractCode(decoded);
          if (target === "code") {
            setCode(c);
          } else {
            setFriendIdentifier(c);
          }
          stopScan();
        },
        () => {
          /* per-frame decode misses — required no-op */
        },
      );
    } catch {
      setScanTarget(null);
      setBanner({
        kind: "error",
        text: "Couldn't start the camera — check permissions or type the code.",
      });
    }
  };

  // Stop the camera if the component unmounts mid-scan.
  useEffect(() => {
    return () => {
      if (qrRef.current) {
        qrRef.current.stop().catch(() => {});
        qrRef.current = null;
      }
    };
  }, []);

  // ---- Generated-ticket QR: download / print -------------------------------
  const downloadGeneratedQr = () => {
    if (!generatedQr) {
      return;
    }
    const a = document.createElement("a");
    a.href = generatedQr.dataUrl;
    a.download = `ticket-${generatedQr.code}.png`;
    a.click();
  };

  const printGeneratedQr = () => {
    if (!generatedQr) {
      return;
    }
    const w = window.open("", "_blank", "width=420,height=600");
    if (!w) {
      return;
    }
    w.document.write(
      `<html><head><title>Ticket ${generatedQr.code}</title></head>` +
        `<body style="font-family:system-ui;text-align:center;padding:24px;">` +
        `<img src="${generatedQr.dataUrl}" style="width:300px;height:300px" />` +
        `<div style="font-size:22px;font-weight:700;letter-spacing:2px;margin-top:12px;">${generatedQr.code}</div>` +
        `<div style="color:#555;margin-top:4px;">USLS Gun Show ticket</div>` +
        `</body></html>`,
    );
    w.document.close();
    w.focus();
    w.print();
  };

  // Mirror the server-side canonicalization (friend_joined ⟹ brought_friend ⟹
  // attended) in the UI so the rep can't even compose an incoherent combo. The
  // RPC enforces it regardless; this is for clarity, not trust.
  const onToggleAttended = (checked: boolean) => {
    setAttended(checked);
    if (!checked) {
      setBroughtFriend(false);
      setFriendJoined(false);
      setFriendIdentifier("");
    }
  };
  const onToggleBrought = (checked: boolean) => {
    setBroughtFriend(checked);
    if (checked) {
      setAttended(true);
    } else {
      setFriendJoined(false);
      setFriendIdentifier("");
    }
  };
  const onToggleFriend = (checked: boolean) => {
    setFriendJoined(checked);
    if (checked) {
      setBroughtFriend(true);
      setAttended(true);
    } else {
      setFriendIdentifier("");
    }
  };

  const handleLookup = async () => {
    if (!code.trim()) {
      return;
    }
    setGeneratedQr(null);
    setLookupBusy(true);
    setLookupMsg("");
    setBanner(null);
    setLastResult(null);
    const res = await lookupCheckinVoucher(code);
    setLookupBusy(false);
    if (res.found && res.voucher) {
      setMember({
        contact: res.contact || {},
        voucherCode: res.voucher.voucher_code,
        eventId: res.voucher.event_id,
        activationState: res.voucher.activation_state,
        currentValue: res.voucher.current_value,
        blockReason: res.blockReason,
      });
      // If the voucher knows its event and none is selected, adopt it.
      if (!eventId && res.voucher.event_id) {
        setEventId(res.voucher.event_id);
      }
    } else {
      setMember(null);
      if (res.failureKind === "network") {
        setLookupMsg(
          "Offline — member lookup unavailable. You can still submit the check-in; it will sync when back online.",
        );
      } else {
        setLookupMsg(res.message || "No voucher found.");
      }
    }
  };

  const handleGenerateTicket = async () => {
    // Generate errors use the center popup (the bottom toast was easy to miss,
    // which read as "nothing happened" when input was rejected).
    if (!eventId) {
      setConfirm({ kind: "error", text: "Select the event first." });
      return;
    }
    if (!genEmail.trim()) {
      setConfirm({ kind: "error", text: "Enter the member's email." });
      return;
    }
    if (!isValidEmail(genEmail)) {
      setConfirm({ kind: "error", text: "Enter a valid email address." });
      return;
    }
    if (!genFirst.trim()) {
      setConfirm({ kind: "error", text: "Enter the member's first name." });
      return;
    }
    // Phone is optional, but if provided it must be a valid 10-digit US number.
    const genPhoneNormalized = genPhone.trim()
      ? normalizeUsPhone(genPhone)
      : "";
    if (genPhone.trim() && !genPhoneNormalized) {
      setConfirm({
        kind: "error",
        text: "Enter a valid 10-digit phone number, or leave it blank.",
      });
      return;
    }
    setGenBusy(true);
    setBanner(null);
    setConfirm(null);
    const res = await generateTicket({
      event_id: eventId,
      email: genEmail.trim(),
      first_name: genFirst,
      last_name: genLast,
      phone: genPhoneNormalized || "",
      client_idempotency_key: newCheckinIdempotencyKey(),
    });
    setGenBusy(false);
    if (res.success && res.data) {
      // Capture the member's email/name BEFORE clearing the form (needed for the
      // durable "forward to a friend" email).
      const memberEmail = genEmail.trim();
      const memberName = `${genFirst} ${genLast}`.trim();
      // Forward-to-a-friend link. Prefer the member's DURABLE referral token (one stable link
      // per member, valid across every conversion event they work) over the per-voucher token;
      // the per-voucher token remains a valid fallback if the durable one can't be fetched.
      // Best-effort: NEVER block or fail the ticket flow if the lookup errors/offline.
      let referralToken: string | undefined = res.data.referral_token;
      try {
        const contactId = res.data.contact?.id;
        if (contactId) {
          const { data: durableToken } = await supabase.rpc(
            "get_member_referral_token",
            { p_contact_id: contactId },
          );
          if (durableToken && typeof durableToken === "string") {
            referralToken = durableToken;
          }
        }
      } catch {
        /* best-effort — fall back to the per-voucher token */
      }
      const referralUrl = referralToken
        ? `${window.location.origin}/refer/${referralToken}`
        : undefined;

      // Drop the new code into the check-in flow and show the member.
      setCode(res.data.voucher_code);
      setMember({
        contact: res.data.contact,
        voucherCode: res.data.voucher_code,
        eventId,
        activationState: "inactive",
        currentValue: 0,
      });
      setShowGenerate(false);
      setGenEmail("");
      setGenFirst("");
      setGenLast("");
      setGenPhone("");

      // Step 3: durable delivery — email the member their voucher + referral link.
      // Best-effort: NEVER block or fail the ticket flow if the send errors/offline.
      let emailedTo: string | undefined;
      if (memberEmail && referralUrl) {
        try {
          // Sent via the service-role wrapper (send-conversion-member-email) so the
          // transactional override reaches suppressed walk-ins regardless of the rep's
          // role (the rep JWT couldn't authorize override_suppression directly).
          // Best-effort: NEVER block or fail the ticket flow if the send errors/offline.
          const { data: emailResp, error: emailErr } =
            await supabase.functions.invoke("send-conversion-member-email", {
              body: {
                to: memberEmail,
                toName: memberName || undefined,
                voucherCode: res.data.voucher_code,
                referralUrl,
                contactId: res.data.contact?.id,
              },
            });
          if (!emailErr && (emailResp as { ok?: boolean } | null)?.ok) {
            emailedTo = memberEmail;
          }
        } catch {
          /* best-effort — ignore email failures */
        }
      }

      // Render the ticket's QR in-app (encode the bare code — the booth scanner
      // and a plain scan both read it) so the rep can show/print/download it.
      // Also render a QR of the referral link as a secondary share prompt.
      try {
        const dataUrl = await QRCode.toDataURL(res.data.voucher_code, {
          width: 600,
          margin: 2,
        });
        let referralDataUrl: string | undefined;
        if (referralUrl) {
          try {
            referralDataUrl = await QRCode.toDataURL(referralUrl, {
              width: 600,
              margin: 2,
            });
          } catch {
            referralDataUrl = undefined;
          }
        }
        setGeneratedQr({
          code: res.data.voucher_code,
          dataUrl,
          reused: res.data.reused,
          referralUrl,
          referralDataUrl,
          emailedTo,
        });
      } catch {
        setGeneratedQr(null);
      }
      setBanner({
        kind: "success",
        text: emailedTo
          ? `Ticket ready: ${res.data.voucher_code}. Voucher + referral link emailed to ${emailedTo}. Show/print the QR below, or mark attendance and submit.`
          : `Ticket ready: ${res.data.voucher_code}. Show/print the QR below, or mark attendance and submit.`,
      });
    } else {
      setConfirm({
        kind: "error",
        text: res.message || "Could not generate a ticket.",
      });
    }
  };

  // Conversion-eligibility of events. An event is eligible if it has its own engine
  // or its type matches an active engine. We only JUDGE eligibility once we have the
  // data (online + loaded ok) — offline, still-loading, or engine-load-error we trust
  // the previously-chosen event for the EXISTING-voucher flow (fail open), but never
  // for generate-ticket (that's gated separately below).
  const isEligible = useCallback(
    (ev: BoothEvent | undefined | null): boolean =>
      Boolean(ev?.conversion_engine_id) ||
      Boolean(ev?.type && engineTypes.includes(ev.type)),
    [engineTypes],
  );
  // We could not load the active-engine list (online) — eligibility is unknown.
  const eligibilityUnknown = online && enginesLoaded && engineLoadError;
  const haveEligibilityData =
    online && enginesLoaded && !engineLoadError && events.length > 0;
  const eligibleEvents = haveEligibilityData
    ? events.filter(isEligible)
    : events;
  const selectedEvent = events.find((e) => e.id === eventId) || null;
  // Chosen event is set but not eligible — covers BOTH a loaded-but-ineligible
  // event (e.g. a seminar) AND an unknown/stale id not in the loaded set.
  const selectedIneligible =
    haveEligibilityData && eventId !== "" && !isEligible(selectedEvent);
  // Sub-case of the above: the id isn't in the loaded events at all (stale URL /
  // localStorage). The select would otherwise look blank with hidden state.
  const selectedUnknown = selectedIneligible && !selectedEvent;
  // Generate-ticket needs an online connection AND a known-eligible event AND a
  // loaded engine list. Any of those missing → block generate (lookup still works).
  const generateBlocked = !online || selectedIneligible || eligibilityUnknown;
  // Collapse the event selector once a verified-eligible event is chosen (guards for
  // ineligible/unknown/engine-error keep it expanded so the rep can see them).
  const selectorCollapsed = shouldCollapseSelector({
    haveEligibilityData,
    hasSelectedEvent: Boolean(selectedEvent),
    selectedIneligible,
    expanded: selectorExpanded,
  });

  // A bare (no-voucher) check-in must NOT run against a stale/ineligible event the
  // rep can't even see selected. When a voucher IS looked up, member.eventId (the
  // voucher's own event, governed by the RPC) is authoritative regardless.
  const effectiveEventId =
    member?.eventId || (selectedIneligible ? "" : eventId);

  const handleSubmit = async () => {
    // Pre-submit blocks use the center popup so a rep who taps Submit always sees
    // WHY nothing was saved (the bottom toast was easy to miss on mobile).
    if (!effectiveEventId) {
      setConfirm({
        kind: "error",
        text: selectedIneligible
          ? "That event can't be used for a no-code check-in. Pick a Conversion Engine event above, or scan/look up a voucher code first."
          : "Select the event first.",
      });
      return;
    }
    if (!code.trim()) {
      setConfirm({ kind: "error", text: "Enter a voucher code." });
      return;
    }
    if (!attended && !broughtFriend && !friendJoined) {
      setConfirm({ kind: "error", text: "Toggle at least one outcome." });
      return;
    }
    if (member?.blockReason) {
      setConfirm({ kind: "error", text: member.blockReason });
      return;
    }
    // Stage 3a: a joined friend must be IDENTIFIED, and linking is online-only.
    // Online with "friend joined" but no identifier → block (don't create a blind
    // catalyst). We also never send friend_joined to the check-in RPC at all (which
    // would record an unidentified catalyst); the friend is recorded ONLY via
    // linkFriend, with identity.
    const friendId = friendIdentifier.trim();
    if (friendJoined && online && !friendId) {
      setConfirm({
        kind: "error",
        text: "Enter the friend's email or code, or uncheck “Friend joined”.",
      });
      return;
    }
    // If the friend identifier looks like an email, validate its shape (a typo'd
    // address would silently fail to link); otherwise it's treated as a code.
    if (
      friendJoined &&
      online &&
      friendId.includes("@") &&
      !isValidEmail(friendId)
    ) {
      setConfirm({
        kind: "error",
        text: "Enter a valid friend email address, or their ticket code.",
      });
      return;
    }

    setSubmitBusy(true);
    setBanner(null);
    setConfirm(null);

    const payload: CheckinInput = {
      event_id: effectiveEventId,
      voucher_code: code.trim().toUpperCase(),
      attended,
      brought_friend: broughtFriend,
      friend_joined: false, // never record a friend_joined fact without identity
      client_idempotency_key: newCheckinIdempotencyKey(),
    };
    const label = memberLabel(member?.contact);

    // Offline → queue the check-in. Friend LINKING can't happen offline (it needs
    // to resolve the friend + uniqueness server-side), so we do NOT pretend the
    // identifier will sync — we warn explicitly and the rep links when back online.
    if (!online) {
      const entry = enqueueCheckin(payload, { memberLabel: label });
      setSubmitBusy(false);
      refreshQueueCount();
      if (entry) {
        setConfirm({
          kind: friendJoined ? "error" : "queued",
          text:
            `Offline — check-in for ${label} queued. It will sync automatically.` +
            (friendJoined
              ? " The joined friend was NOT linked (linking needs a connection) — keep this open and link them here once you're back online."
              : ""),
        });
        // Keep the form when a friend link is still pending so the typed
        // identifier isn't silently discarded; otherwise advance to next member.
        if (shouldResetAfterCheckin({ kind: "offline_queued", friendJoined })) {
          resetForNext();
        }
      } else {
        setConfirm({
          kind: "error",
          text: "Offline queue is full (25). Reconnect to sync before adding more.",
        });
      }
      return;
    }

    // Online → submit; on a NETWORK failure, fall back to the queue.
    const inviterCode = payload.voucher_code;
    const wantsLink = friendJoined && friendId.length > 0;
    const res = await submitCheckin(payload);
    if (res.success && res.data) {
      setLastResult(res.data);
      const v = res.data.current_value;
      const escalated = res.data.value_escalated;
      let text = escalated
        ? `${label} checked in. Voucher is now ${res.data.activation_state} at $${Number(v ?? 0).toFixed(2)}.`
        : `${label} checked in.`;
      let kind: "success" | "error" = "success";

      // Stage 3a — link the joined friend to this inviter (value moves at signup).
      // linkOk stays false until the link genuinely succeeds, so a failed/lost link
      // does NOT reset the form — the rep keeps the typed identifier and can submit
      // again (the check-in replays idempotently and the link is retried).
      let linkOk = true;
      if (wantsLink) {
        linkOk = false;
        const isEmail = friendId.includes("@");
        const link = await linkFriend({
          inviter_voucher_code: inviterCode,
          new_member_email: isEmail ? friendId : undefined,
          new_member_voucher_code: isEmail ? undefined : friendId,
          event_id: effectiveEventId,
          client_idempotency_key: newCheckinIdempotencyKey(),
        });
        if (link.success && link.data) {
          linkOk = true;
          const fname = memberLabel(link.data.new_member);
          text += link.data.friend_already_active
            ? ` Friend ${fname} linked (already a member — reward queued).`
            : ` Friend ${fname} linked; reward applies when they join USLS.`;
        } else {
          kind = "error";
          text += ` (Friend NOT linked: ${link.message}. Fix the friend's email/code and submit again — the check-in won't double-count.)`;
        }
      }
      setSubmitBusy(false);
      setConfirm({ kind, text });
      if (shouldResetAfterCheckin({ kind: "online_success", linkOk })) {
        resetForNext();
      }
    } else if (res.failureKind === "network") {
      setSubmitBusy(false);
      const entry = enqueueCheckin(payload, { memberLabel: label });
      refreshQueueCount();
      if (!entry) {
        setConfirm({
          kind: "error",
          text: "Network issue and the offline queue is full (25).",
        });
      } else if (wantsLink) {
        // The queued check-in carries facts only — it can't link the friend. Don't
        // pretend it's fully saved; keep the form so the friend can be linked on
        // reconnect (submit again — the queued check-in replays idempotently).
        setConfirm({
          kind: "error",
          text: `Network issue — check-in for ${label} queued and will retry, but the joined friend was NOT linked. Keep this open; once you're back online, submit again to link them.`,
        });
      } else {
        setConfirm({
          kind: "queued",
          text: `Network issue — check-in for ${label} queued and will retry.`,
        });
        resetForNext();
      }
    } else {
      setSubmitBusy(false);
      setConfirm({ kind: "error", text: res.message || "Check-in failed." });
    }
  };

  const retryQueue = async () => {
    const outcome = await processCheckinQueue();
    refreshQueueCount();
    setBanner({
      kind: outcome.succeeded > 0 ? "success" : "error",
      text:
        outcome.succeeded > 0
          ? `${outcome.succeeded} synced, ${outcome.remaining} remaining.`
          : `Nothing synced. ${outcome.remaining} still queued.`,
    });
  };

  // Per-row retry — resets a (possibly failed) entry to pending and re-runs the
  // queue. `retryEntry` exists because processQueue alone skips status='failed'.
  const retryOneQueued = async (id: string) => {
    const outcome = await retryQueueEntry(id);
    refreshQueueCount();
    if (!outcome) {
      return;
    }
    setBanner({
      kind: outcome.succeeded > 0 ? "success" : "error",
      text:
        outcome.succeeded > 0
          ? `Synced. ${outcome.remaining} still queued.`
          : `Still not synced (${outcome.remaining} queued). ${
              outcome.businessFailures > 0
                ? "It was rejected by the server — a retry won't help."
                : "Check the connection and try again."
            }`,
    });
  };

  // Dismiss permanently discards local retry state for that item.
  const dismissOneQueued = (id: string) => {
    removeQueueEntry(id);
    refreshQueueCount();
  };

  const queueSummary = summarizeQueue(queueItems);
  const queueNow = Date.now();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Minimal standalone top bar (no CRM sidebar — this is a full-screen booth mode) */}
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="text-base font-bold text-gray-900">
            Booth Check-in
          </span>
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
              online
                ? "bg-green-100 text-green-800"
                : "bg-amber-100 text-amber-800"
            }`}
          >
            <span
              className={`h-2 w-2 rounded-full ${online ? "bg-green-500" : "bg-amber-500"}`}
            />
            {online ? "Online" : "Offline"}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleRefresh}
            className="flex items-center gap-1 text-sm font-medium text-blue-600"
            aria-label="Refresh — clear the form for the next person"
            title="Refresh — clear the form for the next person"
          >
            <span aria-hidden className="text-base leading-none">
              ↻
            </span>
            Refresh
          </button>
          <Link to={exitTo} className="text-sm font-medium text-blue-600">
            Exit
          </Link>
        </div>
      </header>

      {/* QR scanner viewport (shown only while scanning) */}
      {scanTarget && (
        <div className="border-b border-gray-200 bg-black/90 px-4 py-3">
          <div
            id="qr-reader"
            className="mx-auto max-w-xs overflow-hidden rounded-lg"
          />
          <button
            onClick={stopScan}
            className="mt-2 w-full rounded-lg bg-white/90 px-4 py-2 text-sm font-semibold text-gray-900"
          >
            Cancel scan
          </button>
        </div>
      )}

      <div className={`mx-auto max-w-md px-4 py-4 ${banner ? "pb-28" : ""}`}>
        {/* Offline queue — inspection panel. Pending and FAILED counts are shown
            distinctly; expand to retry/dismiss individual rows. */}
        {queueCount > 0 && (
          <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 text-sm text-amber-900">
            <div className="flex items-center justify-between px-3 py-2">
              <button
                type="button"
                onClick={() => setQueueOpen((o) => !o)}
                className="flex items-center gap-2 text-left font-medium"
                aria-expanded={queueOpen}
              >
                <span className="text-xs">{queueOpen ? "▾" : "▸"}</span>
                <span>
                  {queueSummary.pending > 0 &&
                    `${queueSummary.pending} waiting to sync`}
                  {queueSummary.pending > 0 && queueSummary.failed > 0 && " · "}
                  {queueSummary.failed > 0 && (
                    <span className="text-red-700">
                      {queueSummary.failed} failed
                    </span>
                  )}
                </span>
              </button>
              {online && queueSummary.pending > 0 && (
                <button
                  onClick={retryQueue}
                  className="ml-3 shrink-0 rounded bg-amber-600 px-2.5 py-1 text-xs font-semibold text-white"
                >
                  Retry all
                </button>
              )}
            </div>
            {queueOpen && (
              <ul className="divide-y divide-amber-200 border-t border-amber-200">
                {queueItems.map((q) => {
                  const failed = q.status === "failed";
                  return (
                    <li key={q.id} className="px-3 py-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate font-medium text-gray-900">
                            {q.displayContext?.memberLabel || "Member"}
                          </p>
                          <p className="truncate text-xs text-gray-600">
                            {q.voucherCode} ·{" "}
                            <span
                              className={
                                failed ? "text-red-700" : "text-amber-800"
                              }
                            >
                              {failed ? "Failed" : "Waiting"}
                            </span>{" "}
                            · {relativeAge(q.enqueuedAt, queueNow)}
                          </p>
                          {failed && q.lastError && (
                            <p className="mt-0.5 text-xs text-red-700">
                              {q.lastError}
                            </p>
                          )}
                        </div>
                        <div className="flex shrink-0 gap-1.5">
                          {online && (
                            <button
                              type="button"
                              onClick={() => retryOneQueued(q.id)}
                              className="rounded border border-amber-400 bg-white px-2 py-1 text-xs font-semibold text-amber-800"
                            >
                              Retry
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => dismissOneQueued(q.id)}
                            className="rounded border border-gray-300 bg-white px-2 py-1 text-xs font-semibold text-gray-600"
                          >
                            {failed ? "Dismiss failed" : "Dismiss"}
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {/* Event selector */}
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Event
        </label>
        {selectorCollapsed && selectedEvent ? (
          /* Collapsed: a verified-eligible event is chosen — show a compact summary
             with a Change affordance to re-expand, saving phone screen space. */
          <div className="mb-3 flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
            <span className="flex min-w-0 items-center gap-2 text-sm">
              <span className="truncate font-medium text-gray-900">
                {selectedEvent.title}
              </span>
              <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
                {eventTypeLabel(selectedEvent.type)}
              </span>
            </span>
            <button
              type="button"
              onClick={() => setSelectorExpanded(true)}
              className="shrink-0 text-sm font-medium text-blue-600"
            >
              Change
            </button>
          </div>
        ) : (
          <>
            <select
              value={eventId}
              onChange={(e) => {
                setEventId(e.target.value);
                // After picking, re-collapse (the new event collapses if eligible;
                // an ineligible/unknown one keeps the selector + guard expanded).
                setSelectorExpanded(false);
              }}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-base mb-1"
            >
              <option value="">Select event…</option>
              {/* Stale/ineligible selection that isn't in the eligible list: render it
                  as a disabled option so the control shows the real (bad) state instead
                  of silently snapping to the placeholder while eventId still holds it. */}
              {eventId !== "" &&
                !eligibleEvents.some((e) => e.id === eventId) && (
                  <option value={eventId} disabled>
                    {selectedEvent
                      ? `${selectedEvent.title} — not eligible`
                      : "Previously selected event (unavailable)"}
                  </option>
                )}
              {eligibleEvents.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.title}
                  {ev.type ? ` — ${eventTypeLabel(ev.type)}` : ""}
                </option>
              ))}
            </select>
            {/* Explicit selected-event type — a native mobile <select> can truncate the
                "— type" suffix off the chosen option, so the rep may not notice they
                picked a seminar/class. Surface the type as its own unmissable chip. */}
            {selectedEvent && (
              <p className="mb-1 flex items-center gap-1.5 text-xs text-gray-600">
                <span>Event type:</span>
                <span
                  className={`rounded-full px-2 py-0.5 font-medium ${
                    selectedIneligible
                      ? "bg-amber-100 text-amber-900"
                      : "bg-emerald-100 text-emerald-800"
                  }`}
                >
                  {eventTypeLabel(selectedEvent.type)}
                </span>
              </p>
            )}
            {!online && eventId && events.length === 0 && (
              <p className="text-xs text-gray-500 mb-3">
                Using the event selected earlier (offline).
              </p>
            )}
            {/* Eligibility guards (defense in depth for stale storage / URL / linked
                event that isn't a Conversion Engine event, or an engine list we couldn't
                load). None of these block code lookup + check-in — those stay governed by
                the voucher/RPC; they only gate generate-ticket and bare no-code submit. */}
            {selectedUnknown && (
              <p className="mb-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                The previously selected event is no longer available. Pick a
                Conversion Engine event above, or scan/enter an existing voucher
                code (check-in still works by code).
              </p>
            )}
            {selectedIneligible && !selectedUnknown && (
              <p className="mb-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                This event isn't set up for Conversion Engine rewards, so you
                can't generate a ticket for it. Pick a Gun Show event above — or
                scan/enter an existing voucher code (check-in still works by
                code).
              </p>
            )}
            {eligibilityUnknown && (
              <p className="mb-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                Couldn't load the Conversion Engine event list. You can still
                check members in by scanning/entering a voucher code; generating
                a ticket is unavailable until this loads.
              </p>
            )}
            {haveEligibilityData && eligibleEvents.length === 0 && (
              <p className="mb-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                No Conversion Engine events are set up yet. You can still check
                members in by scanning/entering an existing voucher code.
              </p>
            )}
          </>
        )}

        {/* Code entry */}
        <label className="block text-sm font-medium text-gray-700 mb-1 mt-4">
          Voucher code
        </label>
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleLookup();
            }
          }}
          placeholder="ABCD1234"
          autoCapitalize="characters"
          autoCorrect="off"
          className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-base font-mono tracking-wider uppercase"
        />
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            onClick={() => startScan("code")}
            aria-label="Scan QR code"
            className="flex-1 rounded-lg border border-gray-300 px-3 py-2.5 text-sm font-semibold text-gray-700"
          >
            Scan QR
          </button>
          <button
            onClick={handleLookup}
            disabled={lookupBusy || !code.trim() || !online}
            className="flex-1 rounded-lg bg-gray-900 px-3 py-2.5 text-sm font-semibold text-white disabled:opacity-40"
          >
            {lookupBusy ? "…" : "Look up"}
          </button>
        </div>
        {lookupMsg && <p className="mt-2 text-sm text-gray-600">{lookupMsg}</p>}

        {/* No-code walk-in → generate a ticket (online + eligible event only) */}
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setShowGenerate((s) => !s)}
            disabled={generateBlocked}
            className="text-sm font-medium text-blue-600 disabled:text-gray-400"
          >
            {showGenerate ? "Cancel" : "No code? Generate a ticket"}
          </button>
          {!online && (
            <span className="ml-2 text-xs text-gray-500">
              (available online)
            </span>
          )}
          {online && selectedIneligible && (
            <span className="ml-2 text-xs text-gray-500">
              (pick a Gun Show event)
            </span>
          )}
          {online && !selectedIneligible && eligibilityUnknown && (
            <span className="ml-2 text-xs text-gray-500">
              (event list unavailable)
            </span>
          )}
        </div>

        {showGenerate && !generateBlocked && (
          <div className="mt-2 space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div>
              <p className="text-sm font-semibold text-gray-800">
                The member's details (walk-in, no ticket code)
              </p>
              <p className="text-xs text-gray-500">
                Enter the <strong>member's</strong> info to create their ticket.
                A friend (if any) is identified separately after check-in.
              </p>
            </div>
            <input
              value={genEmail}
              onChange={(e) => setGenEmail(e.target.value)}
              placeholder="Member email *"
              type="email"
              autoCapitalize="off"
              autoCorrect="off"
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-base"
            />
            <div className="flex gap-2">
              <input
                value={genFirst}
                onChange={(e) => setGenFirst(e.target.value)}
                placeholder="Member first name *"
                className="w-full min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2.5 text-base"
              />
              <input
                value={genLast}
                onChange={(e) => setGenLast(e.target.value)}
                placeholder="Member last name"
                className="w-full min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2.5 text-base"
              />
            </div>
            <input
              value={genPhone}
              onChange={(e) => setGenPhone(e.target.value)}
              placeholder="Member phone (10 digits, optional)"
              type="tel"
              inputMode="numeric"
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-base"
            />
            <button
              onClick={handleGenerateTicket}
              disabled={genBusy || !genEmail.trim() || !genFirst.trim()}
              className="w-full rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-40"
            >
              {genBusy ? "Creating…" : "Generate ticket"}
            </button>
          </div>
        )}

        {/* Member card */}
        {member && (
          <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <p className="font-semibold text-gray-900">
              {memberLabel(member.contact)}
            </p>
            {member.contact.email && (
              <p className="text-sm text-gray-600">{member.contact.email}</p>
            )}
            {member.contact.phone && (
              <p className="text-sm text-gray-600">{member.contact.phone}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Voucher {member.voucherCode} ·{" "}
              {member.activationState || "inactive"}
              {member.currentValue !== null && member.currentValue !== undefined
                ? ` · $${Number(member.currentValue).toFixed(2)}`
                : ""}
            </p>
            {/* $0 entry-only clarity — an inactive/$0 conversion voucher is intended,
              not a bug: value is added when a friend joins. */}
            {!member.blockReason &&
              (member.activationState !== "active" ||
                Number(member.currentValue ?? 0) === 0) && (
                <p className="mt-2 rounded bg-blue-50 px-2 py-1 text-xs text-blue-800">
                  Entry-only so far ($0) — this is expected. Reward value is
                  added when the member brings a friend who joins USLS.{" "}
                  <span className="font-medium">
                    No email is sent at check-in.
                  </span>
                </p>
              )}
            {member.blockReason && (
              <p className="mt-2 text-sm font-medium text-red-700">
                {member.blockReason}
              </p>
            )}
          </div>
        )}

        {/* Generated-ticket QR — show/print/download for the member (no external tool) */}
        {generatedQr && (
          <div className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-center">
            <p className="text-sm font-medium text-indigo-900">
              Ticket ready — show or print this QR for the member
            </p>
            <p className="text-xs text-indigo-700">
              {generatedQr.emailedTo
                ? `Voucher + referral link emailed to ${generatedQr.emailedTo}.`
                : "This QR is the ticket."}
            </p>
            <img
              src={generatedQr.dataUrl}
              alt={`QR for ${generatedQr.code}`}
              className="mx-auto mt-2 h-44 w-44 rounded bg-white p-1"
            />
            <p className="mt-1 font-mono text-base font-bold tracking-wider text-gray-900">
              {generatedQr.code}
            </p>
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                onClick={printGeneratedQr}
                className="flex-1 rounded-lg border border-indigo-300 bg-white px-3 py-2 text-sm font-semibold text-indigo-700"
              >
                Print
              </button>
              <button
                type="button"
                onClick={downloadGeneratedQr}
                className="flex-1 rounded-lg border border-indigo-300 bg-white px-3 py-2 text-sm font-semibold text-indigo-700"
              >
                Download
              </button>
            </div>

            {/* Step 3: forward-to-a-friend referral link (secondary; the durable copy
                is the emailed link). Shown when the voucher has a referral_token. */}
            {generatedQr.referralUrl && (
              <div className="mt-3 border-t border-indigo-200 pt-3 text-center">
                <p className="text-sm font-semibold text-indigo-900">
                  Forward to a friend
                </p>
                <p className="text-xs text-indigo-700">
                  When a friend joins through this link, the member gets
                  rewarded.
                </p>
                {generatedQr.referralDataUrl && (
                  <img
                    src={generatedQr.referralDataUrl}
                    alt="Referral link QR"
                    className="mx-auto mt-2 h-32 w-32 rounded bg-white p-1"
                  />
                )}
                <p className="mt-1 break-all text-xs text-indigo-800">
                  {generatedQr.referralUrl}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard
                      ?.writeText(generatedQr.referralUrl as string)
                      .catch(() => {});
                  }}
                  className="mt-2 w-full rounded-lg border border-indigo-300 bg-white px-3 py-2 text-sm font-semibold text-indigo-700"
                >
                  Copy referral link
                </button>
              </div>
            )}
          </div>
        )}

        {/* Outcome toggles */}
        <fieldset className="mt-5 space-y-2">
          <legend className="text-sm font-medium text-gray-700 mb-1">
            What happened?
          </legend>
          {[
            {
              label: "Attended",
              checked: attended,
              set: onToggleAttended,
              hint: "Member came to the booth",
            },
            {
              label: "Brought a friend",
              checked: broughtFriend,
              set: onToggleBrought,
              hint: "Came with a guest (implies attended)",
            },
            {
              label: "Friend joined USLS",
              checked: friendJoined,
              set: onToggleFriend,
              hint: "Recorded now, confirmed at signup",
            },
          ].map((t) => (
            <label
              key={t.label}
              className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-3"
            >
              <input
                type="checkbox"
                checked={t.checked}
                onChange={(e) => t.set(e.target.checked)}
                className="h-5 w-5"
              />
              <span>
                <span className="block text-base text-gray-900">{t.label}</span>
                <span className="block text-xs text-gray-500">{t.hint}</span>
              </span>
            </label>
          ))}
        </fieldset>

        {/* Stage 3a — identify the joined friend (online only). */}
        {friendJoined && (
          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Friend's email or ticket code
            </label>
            {!online && (
              <p className="mb-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                You're offline — a friend can't be linked now (linking needs a
                connection). Submitting records the check-in; this stays open so
                you can link the friend once you're back online.
              </p>
            )}
            <div className="flex gap-2">
              <input
                value={friendIdentifier}
                onChange={(e) => setFriendIdentifier(e.target.value)}
                placeholder="friend@email.com or ABCD1234"
                autoCapitalize="off"
                autoCorrect="off"
                className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2.5 text-base"
              />
              <button
                type="button"
                onClick={() => startScan("friend")}
                aria-label="Scan friend's QR code"
                className="shrink-0 whitespace-nowrap rounded-lg border border-gray-300 px-3 py-2.5 text-sm font-semibold text-gray-700"
              >
                Scan
              </button>
            </div>
            {/* Online: explain what linking does. Offline is covered by the
                prominent amber hint above, so we don't repeat it here. */}
            {online && (
              <p className="mt-1 text-xs text-gray-500">
                Links the friend so this member is credited when the friend
                joins USLS.
              </p>
            )}
          </div>
        )}

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={submitBusy}
          className="mt-5 w-full rounded-lg bg-blue-600 px-4 py-3 text-base font-semibold text-white disabled:opacity-50"
        >
          {submitBusy ? "Submitting…" : "Submit check-in"}
        </button>

        {lastResult && lastResult.catalysts.friend_joined_pending && (
          <p className="mt-2 text-xs text-gray-500">
            “Friend joined” is recorded as pending — the reward is confirmed
            when the guest completes signup.
          </p>
        )}
      </div>

      {/* Result / status banner — STICKY toast (no page auto-scroll). Stays until
          the next action or manual dismiss so the rep always sees it. */}
      {banner && (
        <div className="fixed inset-x-0 bottom-0 z-30 px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <div
            role={banner.kind === "error" ? "alert" : "status"}
            className={`mx-auto flex max-w-md items-start justify-between gap-3 rounded-lg px-3 py-3 text-sm shadow-lg ${
              banner.kind === "success"
                ? "bg-green-50 text-green-800 border border-green-300"
                : banner.kind === "queued"
                  ? "bg-amber-50 text-amber-900 border border-amber-300"
                  : "bg-red-50 text-red-800 border border-red-300"
            }`}
          >
            <span className="flex-1">{banner.text}</span>
            <button
              type="button"
              onClick={() => setBanner(null)}
              aria-label="Dismiss"
              className="shrink-0 text-lg leading-none opacity-60 hover:opacity-100"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Center-screen confirmation for a check-in result — a dismiss-required
          popup so reps (esp. on mobile, where the bottom toast is easy to miss)
          clearly see the outcome and don't re-tap Submit. */}
      {confirm && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4"
          onClick={() => setConfirm(null)}
        >
          <div
            role={confirm.kind === "error" ? "alert" : "status"}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl"
          >
            <div
              className={`mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full text-3xl ${
                confirm.kind === "success"
                  ? "bg-green-100 text-green-700"
                  : confirm.kind === "queued"
                    ? "bg-amber-100 text-amber-700"
                    : "bg-red-100 text-red-700"
              }`}
            >
              {confirm.kind === "success"
                ? "✓"
                : confirm.kind === "queued"
                  ? "⏳"
                  : "!"}
            </div>
            <p className="text-base font-medium text-gray-900">
              {confirm.text}
            </p>
            <button
              type="button"
              onClick={() => setConfirm(null)}
              className={`mt-5 w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-white ${
                confirm.kind === "error" ? "bg-red-600" : "bg-gray-900"
              }`}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
